import { useQuery } from '@tanstack/react-query';

import { Card, Spinner } from '../../components/ui';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../lib/api';

interface DashboardData {
  upcomingEvents: Array<{ id: string; title: string; startTime: string; _count: { registrations: number } }>;
  activeMembers: number;
  duesOverdue: number;
  revenueMtd: number;
}

async function fetchDashboard(): Promise<DashboardData> {
  const [events, members, payments] = await Promise.all([
    api.get('/admin/events', { params: { from: new Date().toISOString(), pageSize: 5 } }),
    api.get('/admin/members', { params: { pageSize: 1 } }),
    api.get('/admin/payments', { params: { pageSize: 200, from: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString() } }),
  ]);
  // TODO(section-4): promote this to a dedicated /admin/dashboard endpoint; for now, compose client-side.
  const revenueMtd = ((payments.data.items as Array<{ amount: number; status: string }>) ?? [])
    .filter((p) => p.status === 'PAID')
    .reduce((sum, p) => sum + p.amount, 0);
  return {
    upcomingEvents: (events.data.items ?? []).slice(0, 5),
    activeMembers: members.data.total ?? 0,
    duesOverdue: 0,
    revenueMtd,
  };
}

export function DashboardPage() {
  const { user } = useAuth();
  const { data, isLoading } = useQuery({ queryKey: ['dashboard'], queryFn: fetchDashboard });

  if (isLoading) return <Spinner />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl">Welcome back, {user?.firstName}</h1>
        <p className="text-ink-muted">Here's what's happening at {user?.org.name}.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard label="Active members" value={data?.activeMembers ?? 0} />
        <StatCard label="Dues overdue" value={data?.duesOverdue ?? 0} />
        <StatCard label="Revenue MTD" value={`$${((data?.revenueMtd ?? 0) / 100).toFixed(2)}`} />
      </div>

      <Card>
        <div className="px-5 py-4 border-b border-cream-300">
          <h2 className="font-display text-xl">Upcoming events</h2>
        </div>
        <ul className="divide-y divide-cream-300">
          {(data?.upcomingEvents ?? []).map((ev) => (
            <li key={ev.id} className="px-5 py-3 flex items-center justify-between">
              <div>
                <div className="font-medium">{ev.title}</div>
                <div className="text-sm text-ink-muted">{new Date(ev.startTime).toLocaleString()}</div>
              </div>
              <div className="text-sm text-ink-muted">{ev._count.registrations} registered</div>
            </li>
          ))}
          {(data?.upcomingEvents?.length ?? 0) === 0 && (
            <li className="px-5 py-6 text-center text-ink-muted">No upcoming events.</li>
          )}
        </ul>
      </Card>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <Card className="px-5 py-4">
      <div className="text-sm text-ink-muted">{label}</div>
      <div className="font-display text-3xl mt-1">{value}</div>
    </Card>
  );
}
